/**
 * Every home guide: one per chore (chores.ts sopId = guide id, easyId = easy.id) and topic guides (one-sink prep, bed
 * making, laundry by item, hygiene, beard trimmings, bathroom readiness). Content lives in guides-food.ts and
 * guides-rooms.ts; the shape and builder in guide-kit.ts.
 */
import { lintProcedure, type EasyProcedure } from "../easy.ts";
import { FOOD_GUIDES } from "./guides-food.ts";
import { ROOM_GUIDES } from "./guides-rooms.ts";
import { GROOMING_GUIDES, groomingGuidesFor } from "./guides-grooming.ts";
import type { HomeGuide } from "./guide-kit.ts";
import { getChore } from "./chores.ts";
import { hasAppliance, type HomeProfile } from "./profile.ts";

export { HOME_SOP_ORDER, needLineSearches, type HomeGuide, type HomeProduct, type NeedLine, type NeedLineInfo } from "./guide-kit.ts";

export const HOME_GUIDES: HomeGuide[] = [...FOOD_GUIDES, ...ROOM_GUIDES, ...GROOMING_GUIDES];

/** Every home guide for a state (only the grooming guides carry state words). Utah's is HOME_GUIDES. */
export const homeGuidesFor = (state?: string | null): HomeGuide[] => (state && state !== "UT" ? [...FOOD_GUIDES, ...ROOM_GUIDES, ...groomingGuidesFor(state)] : HOME_GUIDES);
export const getHomeGuide = (id: string) => HOME_GUIDES.find((g) => g.id === id);
export const getHomeGuideFor = (id: string, state?: string | null) => homeGuidesFor(state).find((g) => g.id === id);
export const HOME_EASY: EasyProcedure[] = HOME_GUIDES.map((g) => g.easy);
export const getHomeEasy = (id: string) => HOME_EASY.find((p) => p.id === id);

/**
 * The 5-minute version of a guide: its `quick` Easy steps, in the guide's order, word for word. Undefined when the guide
 * marks none (the "5-minute version" button is then hidden: we never make one up).
 */
export function quickVersion(guideId: string): EasyProcedure | undefined {
  const g = getHomeGuide(guideId);
  if (!g?.quick) return undefined;
  const steps = [...new Set(g.quick.steps)].sort((a, b) => a - b).map((i) => g.easy.steps[i]).filter((x) => x !== undefined);
  if (!steps.length) return undefined;
  return { ...g.easy, id: `${g.easy.id}-quick`, steps };
}

/** Topic guides that belong to one piece of equipment (chore guides follow their chore's appliesTo). */
const TOPIC_NEEDS: Record<string, (p: HomeProfile) => boolean> = {
  "load-dishwasher": (p) => hasAppliance(p, "dishwasher"),
};

/**
 * Does this guide fit this home? A chore guide fits when its chore applies (gas range → the grates guide, not the
 * induction one; no espresso machine → no espresso guides). A topic guide fits unless it needs equipment the home
 * doesn't have. Rooms shows these first ("In your home") and the rest behind "Other guides".
 */
export function guideFitsHome(g: HomeGuide, p: HomeProfile): boolean {
  const chore = getChore(g.id);
  if (chore) return chore.appliesTo(p);
  return TOPIC_NEEDS[g.id]?.(p) ?? true;
}

/** Guides whose Easy steps fail the plain-words lint (should always be empty; tested). */
export function homeLintIssues() {
  return HOME_GUIDES.flatMap((g) => lintProcedure(g.easy).map((i) => ({ guide: g.id, ...i })));
}

const words = (s: string) => s.toLowerCase().replace(/[’']/g, "").split(/[^a-z0-9]+/).filter((w) => w.length > 2);
const STOP = new Set("how the and for with what when where should does can your you our clean cleaning wash washing often long change store keep put home house wipe out check rinse take empty replace make deep spot get need".split(" "));

/** The guide a question names (title and keywords), best first. */
export function searchHomeGuides(q: string): HomeGuide[] {
  return searchHomeGuidesScored(q).map((x) => x.guide);
}

export function searchHomeGuidesScored(q: string): { guide: HomeGuide; score: number }[] {
  const qs = words(q).filter((w) => !STOP.has(w));
  if (!qs.length) return [];
  const stem = (w: string) => w.replace(/(ing|es|s)$/, "");
  const low = q.toLowerCase();
  const scored = HOME_GUIDES.filter((g) => !g.askMatch || g.askMatch.test(low)).map((g) => {
    const title = words(g.title).map(stem);
    const kw = g.keywords.flatMap(words).map(stem);
    const inTitle = qs.filter((w) => title.includes(stem(w))).length;
    const inKw = qs.filter((w) => kw.includes(stem(w))).length;
    const hits = qs.filter((w) => title.includes(stem(w)) || kw.includes(stem(w))).length;
    const titleWords = [...new Set(words(g.title).filter((w) => !STOP.has(w)))];
    const coverage = titleWords.length ? titleWords.filter((w) => qs.some((q) => stem(q) === stem(w))).length / titleWords.length : 0;
    return { g, hits, score: hits * 10 + inTitle * 3 + inKw + coverage * 2 };
  }).filter((x) => x.hits > 0);
  scored.sort((a, b) => b.score - a.score || (a.g.kind === b.g.kind ? 0 : a.g.kind === "chore" ? -1 : 1));
  return scored.map((x) => ({ guide: x.g, score: x.score }));
}
